/**
 * Can the app reach its server right now?
 *
 * One answer for the whole app, fed by the api layer: a call that got NO
 * answer (timed out, network error, the API mid-deploy) marks us unreachable;
 * any answer at all, even a 500, marks us reachable. While unreachable, a
 * /health ping runs on a backoff until the server is back, then everyone who
 * subscribed with onBack() gets told so they can reload what failed.
 *
 * Why: on a laptop the socket goes dead after sleep or a Wi-Fi switch and the
 * request just hangs. Without this the app sat on a spinner or an empty page
 * for good, and the only way out was to know to reload (owner, Oct 20 2026).
 */
import axios from 'axios';

export type ConnectionState = {
  reachable: boolean;
  /** How many times in a row the ping has failed, for the banner's wording. */
  failures: number;
  /** When we first lost the server (ms), null while reachable. */
  since: number | null;
};

let state: ConnectionState = { reachable: true, failures: 0, since: null };
const listeners = new Set<(s: ConnectionState) => void>();
const backListeners = new Set<() => void>();
let pingTimer: ReturnType<typeof setTimeout> | null = null;
let pinging = false;
let healthUrl = '/api/health';

export function configureConnection(opts: { healthUrl: string }) {
  healthUrl = opts.healthUrl;
}

export function getConnection() { return state; }

export function subscribeConnection(fn: (s: ConnectionState) => void) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Called once each time the server comes back after being unreachable. */
export function onBack(fn: () => void) {
  backListeners.add(fn);
  return () => { backListeners.delete(fn); };
}

function emit() { listeners.forEach(fn => fn(state)); }

/** True when the request never got an answer: timeout, DNS, offline, reset. */
export function isNoResponse(err: any): boolean {
  if (!err || err.isReadOnlyBlock) return false;
  if (err.response) return false;
  if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK' || err.code === 'ETIMEDOUT' || err.code === 'ERR_CANCELED') return err.code !== 'ERR_CANCELED';
  if (axios.isAxiosError?.(err)) return true;
  return err.message === 'Network Error' || /timeout/i.test(String(err.message || ''));
}

export function reportReachable() {
  if (state.reachable) return;
  state = { reachable: true, failures: 0, since: null };
  if (pingTimer) { clearTimeout(pingTimer); pingTimer = null; }
  emit();
  backListeners.forEach(fn => { try { fn(); } catch { /* one listener must not stop the rest */ } });
}

export function reportUnreachable() {
  if (!state.reachable) return;
  state = { reachable: false, failures: 0, since: Date.now() };
  emit();
  schedulePing(2000);
}

function schedulePing(ms: number) {
  if (pingTimer) clearTimeout(pingTimer);
  pingTimer = setTimeout(ping, ms);
}

/** Ask the server once, now. The banner's "Try now" button and the backoff both use it. */
export async function ping() {
  if (pinging) return;
  pinging = true;
  try {
    // Plain axios, not the app instance: no auth header, no interceptors,
    // nothing that could itself report unreachable and loop.
    await axios.get(healthUrl, { timeout: 8000, params: { t: Date.now() } });
    reportReachable();
  } catch {
    if (!state.reachable) {
      state = { ...state, failures: state.failures + 1 };
      emit();
      // 3s, 6s, 12s, then every 15s — fast enough to feel instant when the
      // deploy finishes, slow enough not to hammer a server that is coming up.
      schedulePing(Math.min(15000, 3000 * 2 ** Math.min(state.failures - 1, 2)));
    }
  } finally {
    pinging = false;
  }
}

// The browser knows when the network comes back — ask straight away.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { if (!state.reachable) ping(); });
}
